import { useNavigator } from '@repo/toolkit/use-navigator'
import ActionButton from '@repo/ui/action-button'
import { CommissionIcon, DuplicateIcon, FeesIcon, PlusIcon } from '@repo/ui/icons'
import Modal, { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import { useMutation } from '@tanstack/react-query'
import React, { useState } from 'react'

import { API } from '../../api/apis.config'
import { duplicateOlCommission } from '../../api/ols/commissions/duplicate-ol-commission'
import FilterDrawer from '../../components/elements/drawers/filter-drawer'
import DynamicDataTable, { DynamicTableRef } from '../../components/elements/dynamic-table'
import { countFilters } from '../../components/elements/dynamic-table/utils/countFilters'
import UserNavBar from '../../components/elements/nav-bar'
import OlCommissionForm from '../../components/pages/ols/ol-commissions/ol-commission-form'
import { OlCommissionsFilter } from '../../components/pages/ols/ol-commissions/ol-commissions-filter'
import { mapOlCommissionToInput } from '../../components/pages/ols/utils'
import { Paths } from '../../lib/constants/path'
import { useFilters } from '../../lib/hooks/use-filters'
import { useModalManager } from '../../lib/hooks/use-modal-manager'
import useTranslate from '../../lib/hooks/use-translate'
import {
  OlCommission,
  OlCommissionFilters
} from '../../lib/types/models/ol/commission/ol-commission'
import { modalButtonConfig } from '../../lib/utils/buttons-config/validation-buttons-config'
import {
  transformOlCommissionFiltersToUrl,
  transformUrlToOlCommissionFilters
} from '../../lib/utils/filters-mapping/ol-commission'
import { getCommissionColumns } from '../ol-personal-space/fees/utils'

const initialFilters: OlCommissionFilters = {
  periods: []
}

const OlCommissionListPage = () => {
  const tableRef = React.useRef<DynamicTableRef<OlCommission>>(null)
  const [isTableLoading, setIsTableLoading] = useState(false)
  const { navigate, navigateWithEvent } = useNavigator()
  const { t } = useTranslate()

  const { modalState, selectedData, toggleModal, closeModel } = useModalManager<
    'filters' | 'duplicate',
    OlCommission
  >(['filters', 'duplicate'])

  const { selectedFilter, filter, onFilterChange, resetFilters, onSelectedFilterChange } =
    useFilters<OlCommissionFilters>({
      initialFilters,
      options: { allowSearchParam: true },
      transformFilterToUrl: transformOlCommissionFiltersToUrl,
      transformUrlToFilter: transformUrlToOlCommissionFilters
    })

  const { mutateAsync: duplicateOlCommissionMutation } = useMutation({
    mutationFn: duplicateOlCommission,
    onSuccess() {
      tableRef.current?.refetch()
      toggleModal('duplicate')
    }
  })

  return (
    <div>
      <UserNavBar title={'Fees / Avance / Retainer'} icon={FeesIcon} />
      <div className={'h-4'} />

      <DynamicDataTable<OlCommission, OlCommissionFilters>
        ref={tableRef}
        onLoadingChange={setIsTableLoading}
        title={'Fees / Avance / Retainer'}
        isSearchable={false}
        deleteMessage={{
          name: 'commission',
          attributeName: 'id',
          deleteAll: 'Êtes-vous sûr de vouloir supprimer toutes les commissions?',
          deleteOne: `Êtes-vous sûr de vouloir supprimer la commission?`,
          deleteMany: (data) =>
            `Êtes-vous sûr de vouloir supprimer les ${data.length} commissions sélectionnées?`
        }}
        columns={[
          ...getCommissionColumns('COMMISION', t),
          {
            accessorKey: 'id',
            header: '',
            cell: ({ row }) => (
              <ActionButton
                variant={'primary'}
                icon={DuplicateIcon}
                onClick={(e) => {
                  e.stopPropagation()
                  toggleModal('duplicate', row.original)
                }}
              />
            )
          }
        ]}
        selectable
        allowSearchParam
        onRowClick={(olCommission, event) =>
          navigateWithEvent(
            Paths.OL_COMMISSION_UPDATE_PAGE.replace(':id', olCommission.id.toString()),
            event
          )
        }
        endpoint={API.ols.getAllOlCommissions.url}
        filters={selectedFilter}
        filtersLength={countFilters(filter)}
        version="FULL"
        onFiltersClick={() => toggleModal('filters')}
        actions={[
          {
            type: 'global',
            label: 'Ajouter une nouvelle Fees',
            icon: PlusIcon,
            fn: () => navigate(Paths.OL_COMMISSION_CREATE_PAGE)
          }
        ]}
      />

      <FilterDrawer
        isOpen={modalState.filters}
        selectedFilter={selectedFilter}
        isLoading={isTableLoading}
        onClose={() => closeModel('filters')}
        onReset={resetFilters}
        onApply={() => onSelectedFilterChange({ ...filter })}
        onFilterChange={onFilterChange}
      >
        <OlCommissionsFilter filter={filter} onFilterChange={onFilterChange} />
      </FilterDrawer>

      {selectedData && (
        <Modal
          isOpen={modalState.duplicate}
          title={'Dupliquer les commissions/avance/retainer'}
          icon={CommissionIcon}
          size="medium"
          hideActions
          onCancel={() => closeModel('duplicate')}
        >
          <OlCommissionForm
            mode="duplicate"
            hasBorderTop
            className={MODAL_BODY_CLASSNAME}
            defaultValues={{
              ...mapOlCommissionToInput(selectedData),
              periodIds: [],
              adminFeePerStudent: undefined,
              retainer: undefined,
              advancePayment: undefined
            }}
            buttonsConfig={modalButtonConfig({
              confirmText: 'Dupliquer',
              onConfirm: (values) =>
                duplicateOlCommissionMutation({ id: selectedData.id, ...values }),
              onCancel: () => closeModel('duplicate')
            })}
          />
        </Modal>
      )}
    </div>
  )
}

export default OlCommissionListPage
